import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CITIES = ["Bengaluru", "Dubai", "Both"];
const SERVICES = [
  "Personalised Home Search",
  "Real Estate Investment",
  "Commercial Real Estate",
  "Not sure yet",
];

export const ContactForm = ({ variant = "light", source = "website", compact = false }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", service: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errMsg, setErrMsg] = useState("");

  const dark = variant === "dark";
  const labelCls = dark ? "text-white/70" : "text-navy/70";
  const inputBase = `w-full rounded-xl px-4 py-3 text-sm transition-colors outline-none focus:ring-2 focus:ring-gold/60 ${
    dark
      ? "bg-white/10 border border-white/15 text-white placeholder-white/40"
      : "bg-white border border-black/10 text-navy placeholder-navy/40"
  }`;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your full name";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Please enter a valid email";
    if (form.phone.replace(/\D/g, "").length < 7) e.phone = "Please enter a valid phone";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    setErrMsg("");
    try {
      await axios.post(`${API}/contact`, { ...form, source });
      setStatus("success");
      setForm({ name: "", email: "", phone: "", city: "", service: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrMsg(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="contact-form-success" className={`rounded-2xl p-8 text-center ${dark ? "bg-white/5 border border-white/10" : "bg-white border border-black/5"}`}>
        <CheckCircle2 className="mx-auto text-gold" size={42} strokeWidth={1.5} />
        <h3 className={`mt-4 font-serif text-3xl ${dark ? "text-white" : "text-navy"}`}>Thank you.</h3>
        <p className={`mt-2 text-sm ${dark ? "text-white/70" : "text-navy/70"}`}>
          An advisor from My Hoom will reach out within one business day.
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          data-testid="contact-form-reset"
          className={`mt-6 underline underline-offset-4 text-sm ${dark ? "text-gold" : "text-navy"}`}
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className={compact ? "grid gap-4" : "grid gap-5"} noValidate>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className={`text-xs ${labelCls}`}>Full name *</span>
          <input
            data-testid="contact-input-name"
            type="text"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className={`mt-1.5 ${inputBase}`}
            placeholder="Jane Patel"
          />
          {errors.name && <span role="alert" data-testid="error-name" className="error text-xs text-red-400 mt-1 inline-block">{errors.name}</span>}
        </label>
        <label className="block">
          <span className={`text-xs ${labelCls}`}>Email *</span>
          <input
            data-testid="contact-input-email"
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            className={`mt-1.5 ${inputBase}`}
            placeholder="jane@example.com"
          />
          {errors.email && <span role="alert" data-testid="error-email" className="error text-xs text-red-400 mt-1 inline-block">{errors.email}</span>}
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className={`text-xs ${labelCls}`}>Phone *</span>
          <input
            data-testid="contact-input-phone"
            type="tel"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            className={`mt-1.5 ${inputBase}`}
            placeholder="+91 98xxx xxxxx"
          />
          {errors.phone && <span role="alert" data-testid="error-phone" className="error text-xs text-red-400 mt-1 inline-block">{errors.phone}</span>}
        </label>
        <label className="block">
          <span className={`text-xs ${labelCls}`}>City of interest</span>
          <select
            data-testid="contact-input-city"
            value={form.city}
            onChange={(e) => set("city", e.target.value)}
            className={`mt-1.5 ${inputBase}`}
          >
            <option value="">Select a city</option>
            {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>

      <label className="block">
        <span className={`text-xs ${labelCls}`}>Service interested in</span>
        <select
          data-testid="contact-input-service"
          value={form.service}
          onChange={(e) => set("service", e.target.value)}
          className={`mt-1.5 ${inputBase}`}
        >
          <option value="">Select a service</option>
          {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>

      <label className="block">
        <span className={`text-xs ${labelCls}`}>Tell us a little about what you're looking for</span>
        <textarea
          data-testid="contact-input-message"
          rows={compact ? 3 : 4}
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
          className={`mt-1.5 ${inputBase} resize-none`}
          placeholder="Budget, timeline, or any specific micro-market you're considering..."
        />
      </label>

      {status === "error" && (
        <div data-testid="contact-form-error" className="text-sm text-red-400">{errMsg}</div>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        data-testid="contact-form-submit"
        className="btn-gold w-full sm:w-auto disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {status === "loading" ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={16} />}
        {status === "loading" ? "Sending..." : "Talk to an Advisor"}
      </button>
      <p className={`text-xs ${dark ? "text-white/40" : "text-navy/50"}`}>
        By submitting, you agree to be contacted by a My Hoom advisor. No spam — ever.
      </p>
    </form>
  );
};

export default ContactForm;
